import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import { useTranslation } from 'react-i18next';

interface ReaderSettingsMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  fontSize: 'small' | 'medium' | 'large';
  onFontSizeChange: (size: 'small' | 'medium' | 'large') => void;
  fontFamily: 'serif' | 'sans-serif';
  onFontFamilyChange: (family: 'serif' | 'sans-serif') => void;
  themeMode: 'light' | 'dark' | 'sepia';
  onThemeModeChange: (mode: 'light' | 'dark' | 'sepia') => void;
}

export function ReaderSettingsMenu({
  anchorEl,
  open,
  onClose,
  fontSize,
  onFontSizeChange,
  fontFamily,
  onFontFamilyChange,
  themeMode,
  onThemeModeChange,
}: ReaderSettingsMenuProps) {
  const { t } = useTranslation();

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        sx: { width: 300, p: 3, borderRadius: 3, mt: 1 },
      }}
    >
      <Stack spacing={2.5}>
        {/* Background Theme Option */}
        <Box>
          <Typography variant="caption" display="block" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', mb: 1 }}>
            {t('reader.settings.theme')}
          </Typography>
          <ToggleButtonGroup
            value={themeMode}
            exclusive
            onChange={(_, val) => val && onThemeModeChange(val)}
            fullWidth
            size="small"
          >
            <ToggleButton value="light" sx={{ py: 1, textTransform: 'none' }}>
              {t('reader.settings.theme_light')}
            </ToggleButton>
            <ToggleButton value="sepia" sx={{ py: 1, textTransform: 'none' }}>
              {t('reader.settings.theme_sepia')}
            </ToggleButton>
            <ToggleButton value="dark" sx={{ py: 1, textTransform: 'none' }}>
              {t('reader.settings.theme_dark')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Divider />

        {/* Font Family Selection */}
        <Box>
          <Typography variant="caption" display="block" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', mb: 1 }}>
            {t('reader.settings.font_family')}
          </Typography>
          <ToggleButtonGroup
            value={fontFamily}
            exclusive
            onChange={(_, val) => val && onFontFamilyChange(val)}
            fullWidth
            size="small"
          >
            <ToggleButton value="serif" sx={{ py: 1, textTransform: 'none', fontFamily: 'Georgia, serif' }}>
              Serif
            </ToggleButton>
            <ToggleButton value="sans-serif" sx={{ py: 1, textTransform: 'none', fontFamily: 'system-ui, sans-serif' }}>
              Sans Serif
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Divider />

        {/* Font Size Scaling */}
        <Box>
          <Typography variant="caption" display="block" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', mb: 1 }}>
            {t('reader.settings.font_size')}
          </Typography>
          <ToggleButtonGroup
            value={fontSize}
            exclusive
            onChange={(_, val) => val && onFontSizeChange(val)}
            fullWidth
            size="small"
          >
            <ToggleButton value="small" sx={{ py: 1 }}>
              {t('reader.settings.size_small')}
            </ToggleButton>
            <ToggleButton value="medium" sx={{ py: 1 }}>
              {t('reader.settings.size_medium')}
            </ToggleButton>
            <ToggleButton value="large" sx={{ py: 1 }}>
              {t('reader.settings.size_large')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Stack>
    </Popover>
  );
}
